/**
 * PrescribedItems.tsx — THE ONE STAFF-FACING RENDER OF A PRESCRIBED MEDICATION.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: staff-side prescribed
 * medication display. Used by the clinician review ("Previously issued
 * prescriptions"), the admin subscription drill-down and the patient record —
 * ONE component, so the admin, the clinician and the patient record cannot show
 * three different accounts of one prescription.
 *
 * WHAT IT SHOWS, and why it is a labelled block rather than a one-line summary:
 * the full dispensing detail — medication, strength, formulation, directions,
 * quantity — the same five facts the signed prescription and the pharmacy line
 * carry. The previous one-line summaries (`name dosage ×qty` on the clinician
 * side, `name × qty` on the admin side) could not say which half of a pair was
 * missing, silently dropped empty fields into run-together whitespace, and gave
 * the ADMIN strictly less of the prescription than the clinician saw.
 *
 * ⚠️ ABSENCE IS RENDERED, NEVER HIDDEN AND NEVER FILLED.
 *
 * A field nobody recorded shows the words "Not recorded" in an amber pill that
 * cannot be mistaken for the value itself — never a dash, never a blank cell,
 * never a guess. The colour is secondary: the pill's own TEXT carries the
 * meaning, so it reads correctly in monochrome and to a screen reader.
 *
 * That matters more here than anywhere else on the estate. orders-service's
 * auto-issue lane persisted the literal string "Not recorded" INTO the dosage
 * column, so the un-flagged render printed a placeholder in the same font as
 * real clinical directions with nothing to distinguish them. `prescribedLine.ts`
 * maps that sentinel back to absence; this component is what makes the absence
 * visible.
 *
 * ⚠️ NO STRING IS PARSED TO PRODUCE ANOTHER, AND NO VALUE IS COMPOSED. Every
 * value here is read from its own field on the wire. The one place the portal
 * parses a strength out of a product name — the dispensing console's
 * `parseMedicationStrength` (`pharmacy/OrderDetailPanel.tsx`) — is STILL THERE
 * on `dev` as of 2026-09-13 and is being removed by the prescribing-truth lane.
 * That is the defect, not the pattern to follow: this component must never
 * become the second copy of it.
 *
 * The same rule covers the QUANTITY: it is printed as the SERVER worded it,
 * from the one seam the signed prescription and the Pharmacy Express email
 * already use, and the browser supplies no unit of its own — see
 * `lib/prescribedLine#quantityText`.
 *
 * ⚠️ R28 — NO MEDICINE NAME IN THIS FILE OR ITS SPEC. The repo's own guard
 * (`journey/r28.spec.tsx`) scans every file under `src/`, comments and test
 * fixtures included; the spec beside this file reuses the repo's existing
 * neutral placeholders (`Treatment A`, `testing/fixtures.ts`) for exactly that
 * reason.
 *
 * Design: light-mode `--gps-*` tokens throughout, like every other staff card.
 */
import type { CSSProperties } from 'react';
import type { PrescribedLine } from '../lib/prescribedLine';
import { NOT_RECORDED, isBareProductKey, resolvePrescribedLines } from '../lib/prescribedLine';
import type { PrescribedItem } from '../api/types';

/**
 * The flagged absent state. An amber pill, deliberately styled UNLIKE the value
 * text beside it (smaller, uppercase, pilled, tinted) so no reader can mistake
 * the flag for a recorded value. Same tokens as `ui/kit` Badge `pending`.
 */
const ABSENT_STYLE: CSSProperties = {
  display: 'inline-block',
  background: 'var(--gps-warn-bg)',
  color: 'var(--gps-warn-text)',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '2px 8px',
  borderRadius: 'var(--gps-r-pill)',
  whiteSpace: 'nowrap',
};

const VALUE_STYLE: CSSProperties = {
  color: 'var(--gps-text)',
  fontWeight: 600,
  margin: 0,
  // The prescriber's own words are carried VERBATIM, line breaks included.
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const LABEL_STYLE: CSSProperties = {
  color: 'var(--gps-muted)',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.02em',
  margin: 0,
};

/**
 * THE FLAG, on its own, for a surface that is not a prescribed line but still
 * has to say "nobody recorded this".
 *
 * Exported so a Row/Td elsewhere shows the SAME words in the SAME pill as a
 * prescription field does. The alternative the estate had — an em-dash — is
 * indistinguishable from a hyphen, a loading state and a deliberate blank, and
 * it is what the deal drawer printed where a plan should be.
 *
 * `label` is REQUIRED, not optional, and that is deliberate: it names the field
 * for a screen reader, and an optional one would be omitted at exactly the call
 * sites that are furthest from a visible label.
 */
export function NotRecorded({ label }: { label: string }) {
  return (
    <span style={ABSENT_STYLE}>
      {/* The VISIBLE words live in their own element so they carry the meaning
          on their own (WCAG 1.4.1 — the amber only makes them findable, it is
          never the message). */}
      <span>{NOT_RECORDED}</span>
      {/* WHICH field is missing, for a screen reader. "Not recorded" is
          unambiguous in a two-column record where the label sits beside it and
          completely ambiguous the moment it is read out of that context — and
          a clinical record with four of these in a row is exactly that
          context. No em-dash: this component's own spec asserts that no dash
          appears anywhere in its output, because a dash is how the estate used
          to spell an absence and it is indistinguishable from a hyphen, a
          loading state and a deliberate blank. */}
      <span className="gps-sr-only"> (no {label.toLowerCase()} recorded on this prescription line)</span>
    </span>
  );
}

/**
 * One label/value pair. `value === null` is the ONLY branch that produces the
 * flag, so an absent field can never slip through as an empty string: the
 * resolver already trimmed and sentinel-checked it.
 */
function DetailField({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={{ minWidth: 0 }}>
      <dt style={LABEL_STYLE}>{label}</dt>
      <dd style={{ margin: '2px 0 0' }}>
        {value === null ? <NotRecorded label={label} /> : <span style={VALUE_STYLE}>{value}</span>}
      </dd>
    </div>
  );
}

/**
 * One prescribed line, in full.
 *
 * The product key is shown LAST and labelled as what it is. It is a truthful
 * ops fact (the plan the order was placed against) and it is the only handle a
 * reader has when the dispensing name is missing — but it is not a medication
 * name, so it never occupies the medication row.
 */
export function PrescribedItemDetail({ line, ordinal }: { line: PrescribedLine; ordinal?: number }) {
  return (
    <div
      style={{
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        background: 'var(--gps-surface-2)',
        padding: '10px 12px',
      }}
    >
      {/* THE LINE NUMBER, on multi-item prescriptions only.
          A reader who spots a flag has to be able to say WHICH line it is on —
          to a colleague, to the prescriber, in a note — and "the second box
          down" is not a reference anyone can act on. Suppressed on a
          single-line prescription because "Line 1" with no Line 2 is noise. */}
      {ordinal === undefined ? null : (
        <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: 600, color: 'var(--gps-muted)' }}>
          Line {ordinal}
        </p>
      )}
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '8px 16px',
          margin: 0,
          fontSize: '13.5px',
        }}
      >
        <DetailField label="Medication" value={line.name} />
        <DetailField label="Strength" value={line.strength} />
        <DetailField label="Formulation" value={line.formulation} />
        <DetailField label="Directions" value={line.directions} />
        {/* THE SERVER'S OWN WORDING, rendered verbatim — the SAME string the
            signed prescription and the Pharmacy Express dispensing email
            carry, because all three come out of one orders-side seam.

            This line used to read
              `${line.quantity} pack${line.quantity === 1 ? '' : 's'}`
            which is a browser composing a clinical quantity: a second
            formatter beside the server's, agreeing with it by coincidence
            until one of them changed, and inventing the very unit that is
            unsettled while packQty and the registry's packSize disagree.
            `lib/prescribedLine` now decides it and nothing is composed here.
            Nothing multiplies by packQty. */}
        <DetailField label="Quantity" value={line.quantityText} />
        <DetailField label="Product key" value={line.productKey} />
      </dl>
      {isBareProductKey(line) ? (
        // One plain sentence instead of leaving the reader to infer it from a
        // row of amber pills. It names the cause — this is what an auto-issued
        // month-1 prescription looks like — so a clinician does not read it as
        // a loading failure and retry.
        <p style={{ color: 'var(--gps-muted)', fontSize: '12.5px', margin: '8px 0 0' }}>
          No dispensing detail was recorded for this line. Only the product key above is on file.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Every line on one prescription, or an honest sentence when there are none.
 *
 * `emptyText` differs by surface only in wording, never in honesty: "no items"
 * is a fact about the record, not a placeholder standing in for one.
 */
export function PrescribedItems({
  items,
  emptyText = 'No item detail on file.',
}: {
  items: PrescribedItem[] | null | undefined;
  emptyText?: string;
}) {
  const lines = resolvePrescribedLines(items);
  if (lines.length === 0) {
    return (
      <p style={{ color: 'var(--gps-muted)', fontSize: '13px', margin: 0 }}>{emptyText}</p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: '8px' }}>
      {lines.map((line, i) => (
        // Lines have no id on the wire and a prescription may legitimately
        // carry the same product twice at different strengths, so the index is
        // the only stable key — and the list is never reordered or filtered.
        <PrescribedItemDetail
          key={`${line.productKey ?? 'unkeyed'}-${i}`}
          line={line}
          ordinal={lines.length > 1 ? i + 1 : undefined}
        />
      ))}
    </div>
  );
}
